import {ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {ForwardedRef, forwardRef, MouseEventHandler, useImperativeHandle, useState} from 'react';
import ProductCostsV2, {ProductCostsV2Fields} from '../../../../../models/productCostsV2Model';
import {LVBSize} from '../../../../../util/enums';
import {getProductDimensions} from '../../../../../util/requestSender';
import {getLVBSizeFromDimensions} from '../../../../../util/salesDashboardUtil';
import {ProductCostsErrors} from '../../../../../util/types';
import {createErrorMessage} from '../../../../../util/utils';
import EditProductCosts from './editProductCosts';
import {ActivateHandle} from '../../../../../util/interfaces';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: VoidFunction;
    onDeleteCosts: (costs: ProductCostsV2) => MouseEventHandler;
}

const emptyErrors: ProductCostsErrors = {minQuantity: null};

function EditProductCostsController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const {onSuccess, onDeleteCosts} = props;
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<ProductCostsErrors>(emptyErrors);
    const [productCosts, setProductCosts] = useState<ProductCostsV2 | null>(null);

    function onActivate(costs: ProductCostsV2) {
        setProductCosts(costs);
        loadLVBSize(costs);
    }

    function onClose() {
        setProductCosts(null);
        // Wait for transition
        window.setTimeout(() => {
            setLoading(false);
            setErrors(emptyErrors);
        }, 200);
    }

    function onChange(key: keyof ProductCostsV2Fields) {
        return function (value: number | IDate | string | LVBSize | null) {
            if (!productCosts) return;
            setProductCosts(productCosts.clone().setField(key, value));
        };
    }

    function onResetError(key: keyof ProductCostsErrors) {
        return function () {
            setErrors(prevErrors => ({...prevErrors, [key]: null}));
        };
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        if (!productCosts?.id.length) onCreate();
        else onUpdate();
    }

    function onCreate() {
        if (!productCosts) return;
        productCosts.create((_: ProductCostsV2, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar(
                    t('salesDashboardV2.productDetails.productCosts.costsAdded', 'De productkosten zijn toegevoegd')
                );
                onSuccess();
                onClose();
            } else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t(
                                'salesDashboardV2.productDetails.productCosts.errorAddCosts',
                                'Het toevoegen van de productkosten is niet gelukt.'
                            )
                    ),
                    t('salesDashboardV2.productDetails.productCosts.addFailed', 'Toevoegen mislukt')
                );
        });
    }

    function onUpdate() {
        if (!productCosts) return;
        productCosts.update((_: ProductCostsV2, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar(
                    t('salesDashboardV2.productDetails.productCosts.costsUpdated', 'De productkosten zijn bijgewerkt')
                );
                onSuccess();
                onClose();
            } else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t(
                                'salesDashboardV2.productDetails.productCosts.errorUpdateCosts',
                                'Het bijwerken van de productkosten is niet gelukt.'
                            )
                    ),
                    t('salesDashboardV2.productDetails.productCosts.updateFailed', 'Bijwerken mislukt')
                );
        });
    }

    function loadLVBSize(productCosts: ProductCostsV2) {
        setLoading(true);
        getProductDimensions({
            account_id: productCosts.accountId,
            ean: productCosts.ean,
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const lvbSize = getLVBSizeFromDimensions(data.dimensions);
                    if (lvbSize && productCosts) setProductCosts(productCosts.clone().setField('lvb_size', lvbSize));
                }
            }
        });
    }

    function validateValues(): boolean {
        if (!productCosts) return false;
        let valid = true;
        const _errors = {...errors};
        if (!productCosts.minQuantity) {
            _errors.minQuantity = t(
                'salesDashboardV2.productDetails.productCosts.minQuantityRequired',
                'Minimaal aantal is verplicht'
            );
            valid = false;
        }
        setErrors(_errors);
        return valid;
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <EditProductCosts
            onClose={onClose}
            onChange={onChange}
            onResetError={onResetError}
            onSubmit={onSubmit}
            onDelete={onDeleteCosts}
            productCosts={productCosts}
            loading={loading}
            errors={errors}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(EditProductCostsController);
